import { useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { buildClasses } from "../utils/buildClasses";
import { RyeNoResults } from "../rye/RyeNoResults";
import { RyeMainContentHeader } from "../rye/RyeMainContentHeader";
import { RyeMainContentSpacer } from "../rye/RyeMainContentSpacer";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { RyeSegmentedButton } from "../rye/RyeSegmentedButton";
import { RyeButton } from "../rye/RyeButton";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { useGetGroupedCategoriesForBudget } from "../budgets_screen/useGetGroupedCategoriesForBudget";
import { RyeMainContentSplitPane } from "../rye/RyeMainContentSplitPane";
import { TransactionsDrilldownPane } from "../common/TransactionsDrilldownPane";
import type { CategoryBreakdownReportState } from "./cashFlowState";
import { ReportDateRangeControl } from "./ReportDateRangeControl";
import { resolveDateRange } from "./reportDateRange";
import { BreakdownSelection, TYPE_LABELS } from "./breakdownChartParts";
import { BreakdownDonut } from "./BreakdownDonut";
import { BreakdownTreemap } from "./BreakdownTreemap";
import { BreakdownBars } from "./BreakdownBars";
import { buildBreakdownBars } from "./breakdownBarRows";
import {
  BREAKDOWN_TYPES,
  BreakdownType,
  useBreakdownSlices,
  useCategoryBreakdownData,
} from "./useCategoryBreakdownData";

export function CategoryBreakdownReport({
  state,
  setState,
}: {
  state: CategoryBreakdownReportState;
  setState: (
    update: (
      prev: CategoryBreakdownReportState,
    ) => CategoryBreakdownReportState,
  ) => void;
}): JSX.Element {
  const { dateRange, type, level, chartType, barsType } = state;

  // Memoized so they keep a stable identity across renders — the data hook
  // keys its work off of them.
  const { beginDate, endDate } = useMemo(
    () => resolveDateRange(dateRange, dayjs()),
    [dateRange],
  );

  const data = useCategoryBreakdownData(beginDate, endDate);
  const slices = useBreakdownSlices(data, type, level);

  // The bars show every category in category-list order, which the grouping
  // hook budget tracking uses already provides.
  const groupedCategories = useGetGroupedCategoriesForBudget();
  const categoryGroups = useContext(CategoryGroupsContext);
  const barGroups = useMemo(
    () => buildBreakdownBars(data, groupedCategories, categoryGroups, barsType),
    [data, groupedCategories, categoryGroups, barsType],
  );
  // Groups start expanded. Tracking the collapsed ones, rather than the
  // expanded ones, means groups that appear later start expanded too.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const expandedGroupIds = useMemo(
    () =>
      new Set(
        barGroups
          .map((group) => group.id)
          .filter((groupId) => !collapsedGroupIds.has(groupId)),
      ),
    [barGroups, collapsedGroupIds],
  );
  const allExpanded = barGroups.every((group) =>
    expandedGroupIds.has(group.id),
  );

  function toggleGroup(groupId: string) {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  const [selection, setSelection] = useState<BreakdownSelection | null>(null);

  useEffect(() => {
    setSelection(null);
  }, [beginDate, endDate, type, level, chartType, barsType]);

  return (
    <>
      <RyeMainContentHeader title="Category breakdown" roundedTl={false}>
        <div
          className={buildClasses("flex", "items-center", "gap-3", "w-full")}
        >
          <ReportDateRangeControl
            presets={[
              "this-month",
              "last-month",
              "year-to-date",
              "last-12-months",
            ]}
            range={dateRange}
            onChange={(update) =>
              setState((prev) => ({
                ...prev,
                dateRange: update(prev.dateRange),
              }))
            }
          />
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "gap-3",
              "ml-auto",
              "shrink-0",
            )}
          >
            {chartType === "bars" ? (
              <>
                <RyeButton
                  text={allExpanded ? "Collapse all" : "Expand all"}
                  icon={allExpanded ? "unfold_less" : "unfold_more"}
                  size="sm"
                  onClick={() =>
                    setCollapsedGroupIds(
                      allExpanded
                        ? new Set(barGroups.map((group) => group.id))
                        : new Set(),
                    )
                  }
                />
                <RyeSegmentedButton
                  entries={(["all", ...BREAKDOWN_TYPES] as const).map(
                    (entry) => ({
                      id: entry,
                      displayText: entry === "all" ? "All" : TYPE_LABELS[entry],
                      onClick: () =>
                        setState((prev) => ({ ...prev, barsType: entry })),
                    }),
                  )}
                  initEntryId={barsType}
                />
              </>
            ) : (
              <>
                <RyeSegmentedButton
                  entries={BREAKDOWN_TYPES.map((entry) => ({
                    id: entry,
                    displayText: TYPE_LABELS[entry],
                    onClick: () =>
                      setState((prev) => ({ ...prev, type: entry })),
                  }))}
                  initEntryId={type}
                />
                <RyeSegmentedButton
                  entries={[
                    {
                      id: "groups",
                      displayText: "Groups",
                      onClick: () =>
                        setState((prev) => ({ ...prev, level: "groups" })),
                    },
                    {
                      id: "categories",
                      displayText: "Categories",
                      onClick: () =>
                        setState((prev) => ({ ...prev, level: "categories" })),
                    },
                  ]}
                  initEntryId={level}
                />
              </>
            )}
            <RyeSegmentedButton
              entries={[
                {
                  id: "bars",
                  icon: "align_horizontal_left",
                  tooltip: "Bars",
                  onClick: () =>
                    setState((prev) => ({ ...prev, chartType: "bars" })),
                },
                {
                  id: "donut",
                  icon: "donut_large",
                  tooltip: "Donut",
                  onClick: () =>
                    setState((prev) => ({ ...prev, chartType: "donut" })),
                },
                {
                  id: "treemap",
                  icon: "dashboard",
                  tooltip: "Treemap",
                  onClick: () =>
                    setState((prev) => ({ ...prev, chartType: "treemap" })),
                },
              ]}
              initEntryId={chartType}
            />
          </div>
        </div>
      </RyeMainContentHeader>
      <RyeMainContentSpacer direction="h" />
      <RyeMainContentSplitPane
        bottomPaneLabel="transactions pane"
        bottomPane={
          selection ? (
            <TransactionsDrilldownPane
              onClose={() => setSelection(null)}
              key={selection.id}
              groupLabel={selection.label}
              transactions={selection.transactions}
            />
          ) : null
        }
      >
        <RyeMainContentCard
          roundedTl={false}
          roundedTr={false}
          roundedBl={false} // meets the left nav
          roundedBr={selection === null}
        >
          {chartType === "bars" ? (
            <BreakdownBars
              groups={barGroups}
              expandedGroupIds={expandedGroupIds}
              onToggleGroup={toggleGroup}
              currency={data.currency}
              unreviewedCount={data.unreviewedCount}
              selectedId={selection?.id ?? null}
              onSelect={setSelection}
            />
          ) : slices.length === 0 ? (
            <EmptyState type={type} />
          ) : chartType === "donut" ? (
            <BreakdownDonut
              slices={slices}
              type={type}
              currency={data.currency}
              unreviewedCount={data.unreviewedCount}
              selectedId={selection?.id ?? null}
              onSelect={setSelection}
            />
          ) : (
            <BreakdownTreemap
              slices={slices}
              type={type}
              level={level}
              currency={data.currency}
              unreviewedCount={data.unreviewedCount}
              onSelect={setSelection}
            />
          )}
        </RyeMainContentCard>
      </RyeMainContentSplitPane>
    </>
  );
}

function EmptyState({ type }: { type: BreakdownType }): JSX.Element {
  return (
    <RyeNoResults
      icon="align_horizontal_left"
      title="Nothing to show for these dates"
      description={`There are no reviewed transactions in ${TYPE_LABELS[type].toLowerCase()} categories for this date range.`}
    />
  );
}
